import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Header from "@/pages/home/components/Header";
import Footer from "@/pages/home/components/Footer";
import { supabase } from "@/lib/supabase";

interface VerifiedCompany {
  id: number;
  name: string;
  description: string | null;
  image_url: string | null;
  website_url: string | null;
  category: string | null;
  deposit_amount: string | null;
  registration_code: string | null;
  game_types: string | null;
  features: string | null;
  content: string | null;
  created_at: string;
}

export default function VerifiedDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [company, setCompany] = useState<VerifiedCompany | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const fetchCompany = async () => {
      if (!id) return;
      try {
        setLoading(true);
        setError(false);
        const { data, error: fetchError } = await supabase
          .from("verified_companies")
          .select("*")
          .eq("id", id)
          .single();

        if (cancelled) return;
        if (fetchError) throw fetchError;

        setCompany(data);
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchCompany();
    return () => { cancelled = true; };
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background-50">
        <Header />
        <div className="max-w-[800px] mx-auto px-3 md:px-4 py-8">
          <div className="animate-pulse bg-background-100 rounded-lg border border-background-300 p-6">
            <div className="flex gap-4">
              <div className="w-20 h-20 rounded-lg bg-background-200 flex-shrink-0" />
              <div className="flex-1 space-y-3">
                <div className="h-5 bg-background-200 rounded w-1/3" />
                <div className="h-3 bg-background-200 rounded w-1/2" />
                <div className="h-3 bg-background-200 rounded w-2/3" />
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !company) {
    return (
      <div className="min-h-screen bg-background-50">
        <Header />
        <div className="max-w-[800px] mx-auto px-3 md:px-4 py-20 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 border border-background-300 flex items-center justify-center">
            <span className="w-8 h-8 flex items-center justify-center text-foreground-600">
              <i className="ri-error-warning-line text-2xl" />
            </span>
          </div>
          <p className="text-base text-foreground-600 mb-4">업체 정보를 찾을 수 없습니다</p>
          <Link
            to="/verified"
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary-500 text-foreground-50 rounded-md text-sm hover:bg-primary-600 transition-colors"
          >
            <i className="ri-arrow-left-line" />
            보증업체 목록으로
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const gameTypeList = company.game_types
    ? company.game_types.split(",").map((s) => s.trim()).filter(Boolean)
    : [];

  return (
    <div className="min-h-screen bg-background-50">
      <Header />

      <div className="max-w-[800px] mx-auto px-3 md:px-4 py-6 md:py-8">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-xs text-foreground-600 mb-4">
          <Link to="/" className="hover:text-foreground-400 transition-colors">홈</Link>
          <span className="w-3 h-3 flex items-center justify-center">
            <i className="ri-arrow-right-s-line" />
          </span>
          <Link to="/verified" className="hover:text-foreground-400 transition-colors">보증업체</Link>
          <span className="w-3 h-3 flex items-center justify-center">
            <i className="ri-arrow-right-s-line" />
          </span>
          <span className="text-foreground-400 truncate max-w-[200px]">{company.name}</span>
        </div>

        {/* Main Card */}
        <div className="bg-background-100 rounded-lg border border-background-300 p-5 md:p-6 mb-4">
          <div className="flex items-start gap-4 md:gap-5">
            {/* Thumbnail */}
            <div className="w-28 h-28 md:w-36 md:h-36 rounded-lg overflow-hidden bg-background-200 border border-background-300 flex items-center justify-center flex-shrink-0">
              {company.image_url ? (
                <img
                  src={company.image_url}
                  alt={company.name}
                  className="w-full h-full object-contain"
                />
              ) : (
                <span className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center text-foreground-600">
                  <i className="ri-building-line text-3xl md:text-4xl" />
                </span>
              )}
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h1 className="text-lg md:text-xl font-bold text-foreground-700">{company.name}</h1>
                <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                  <i className="ri-verified-badge-fill" />
                </span>
              </div>

              {company.category && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-secondary-100 text-secondary-900 mb-2">
                  {company.category}
                </span>
              )}

              {company.website_url && (
                <a
                  href={company.website_url}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="text-xs md:text-sm text-primary-500 hover:text-primary-400 block mb-3 truncate"
                >
                  {company.website_url}
                </a>
              )}

              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs md:text-sm text-foreground-600">
                {company.deposit_amount && (
                  <div className="flex items-center gap-1.5">
                    <span className="w-4 h-4 flex items-center justify-center text-accent-500">
                      <i className="ri-money-cny-circle-line" />
                    </span>
                    <span className="font-medium text-foreground-700">보증금 {company.deposit_amount}</span>
                  </div>
                )}
                {company.registration_code && (
                  <div className="flex items-center gap-1.5">
                    <span className="w-4 h-4 flex items-center justify-center text-primary-500">
                      <i className="ri-key-line" />
                    </span>
                    <span className="font-mono bg-background-200 px-2 py-0.5 rounded text-[11px] md:text-xs">
                      가입코드 {company.registration_code}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          {company.website_url && (
            <div className="flex gap-2 mt-5 pt-4 border-t border-background-300">
              <a
                href={company.website_url}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-primary-500 text-foreground-50 rounded-md text-sm font-bold hover:bg-primary-600 transition-colors"
              >
                <i className="ri-external-link-line" />
                사이트 바로가기
              </a>
            </div>
          )}
        </div>

        {/* Game Types */}
        {gameTypeList.length > 0 && (
          <div className="bg-background-100 rounded-lg border border-background-300 p-4 md:p-5 mb-4">
            <h2 className="text-sm font-bold text-foreground-700 mb-3 flex items-center gap-2">
              <span className="w-4 h-4 flex items-center justify-center text-primary-500">
                <i className="ri-gamepad-line" />
              </span>
              제공 게임
            </h2>
            <div className="flex flex-wrap gap-2">
              {gameTypeList.map((gt) => (
                <span
                  key={gt}
                  className="inline-flex items-center px-3 py-1.5 rounded-full text-xs bg-background-200 text-foreground-600 border border-background-300"
                >
                  {gt}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Features */}
        {company.features && (
          <div className="bg-background-100 rounded-lg border border-background-300 p-4 md:p-5 mb-4">
            <h2 className="text-sm font-bold text-foreground-700 mb-3 flex items-center gap-2">
              <span className="w-4 h-4 flex items-center justify-center text-accent-500">
                <i className="ri-star-line" />
              </span>
              주요 특징
            </h2>
            <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{company.features}</p>
          </div>
        )}

        {/* Description / Content */}
        {(company.description || company.content) && (
          <div className="bg-background-100 rounded-lg border border-background-300 p-4 md:p-5 mb-4">
            <h2 className="text-sm font-bold text-foreground-700 mb-3 flex items-center gap-2">
              <span className="w-4 h-4 flex items-center justify-center text-secondary-500">
                <i className="ri-article-line" />
              </span>
              상세 정보
            </h2>
            {company.description && (
              <p className="text-xs md:text-sm text-foreground-600 leading-relaxed mb-3">{company.description}</p>
            )}
            {company.content && (
              <div
                className="text-xs md:text-sm text-foreground-600 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: company.content }}
              />
            )}
          </div>
        )}

        {/* Back */}
        <div className="flex justify-center mt-6">
          <Link
            to="/verified"
            className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-background-200 text-foreground-700 rounded-md text-sm hover:bg-background-300 transition-colors"
          >
            <i className="ri-arrow-left-line" />
            보증업체 목록으로
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  );
}